import React from 'react';
import { connect } from 'react-redux';
import { getActiveLanguage, getTranslate } from 'react-localize-redux';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Typography, Link, Card, CardHeader, Divider, CardContent, Button, Box, Paper } from '@material-ui/core';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import routes from '../../library/Routes';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import { formatDiscount, formatPrice } from '../../utils/format';
import { calculatePriceAfterDiscount } from '../../utils/price';
import {
  LIQ_PAY_PAYMENT_SOURCE,
  MONOBANK_PAYMENT_SOURCE,
  STRIPE_PAYMENT_SOURCE,
} from '../../constants/paymentConstants';
import { withSnackbar, WithSnackbarProps } from 'notistack';

interface RatePlanDiscount {
  ratePlanId: string;
  discount: number;
  discountType: string;
  [key: string]: any;
}

interface StudioClient {
  _id: string;
  discounts?: RatePlanDiscount[];
  [key: string]: any;
}

interface Owner {
  _id: string;
  liqPayPayment?: boolean;
  wayForPayPayment?: boolean;
  monobankAcquiringPayment?: boolean;
  organizationSettings?: {
    stripeConfigs?: {
      apiKey?: string;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

interface Studio {
  _id: string;
  id?: string;
  themeColors?: string[];
  [key: string]: any;
}

interface RatePlan {
  _id: string;
  name: string;
  description?: string;
  price: number;
  color: string;
  discount?: RatePlanDiscount;
  [key: string]: any;
}

interface RatePlansData {
  ratePlans: RatePlan[];
  [key: string]: any;
}

interface RatePlansViewProps extends WithSnackbarProps, WithStyles<typeof styles> {
  studio: Studio;
  owner: Owner;
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  domain: {
    domain: {
      id: string;
      currencyCode: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  studioClient?: StudioClient;
  paymentSource?: string;
}

interface RatePlansViewState {
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  studio: Studio;
  navClass: string;
  ownerDomain?: any;
  ratePlans?: RatePlan[];
  loadingRatePlans?: boolean;
}

class RatePlansView extends React.Component<RatePlansViewProps, RatePlansViewState> {
  private delta: number = 5;
  private navbarHeight: number = 56;
  private headerHeight: number = 56;

  constructor(props: RatePlansViewProps) {
    super(props);
    this.state = {
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      studio: this.props.studio,
      navClass: 'studio-header-full',
    };
  }

  UNSAFE_componentWillMount() {
    this.loadRatePlans(this.props.owner, this.props.studio);
  }

  loadRatePlans = (owner: Owner, studio: Studio) => {
    this.setState({ loadingRatePlans: true });
    let api = serviceLocator.ratePlanApiCall;
    api.getPublicRatePlan(owner._id, studio && studio._id).then((data: RatePlansData) => {
      let ratePlans = data.ratePlans;
      this.setState({ ratePlans: ratePlans, loadingRatePlans: false });
    }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
  };

  getThemeColor = (index: number): string | undefined => {
    if (this.props.studio && this.props.studio.themeColors && this.props.studio.themeColors[index]) {
      return this.props.studio.themeColors[index];
    }
    return undefined;
  };

  findDiscount = (ratePlanId: string): RatePlanDiscount | undefined => {
    if (!this.props.studioClient || !this.props.studioClient.discounts) {
      return undefined;
    }
    return this.props.studioClient.discounts.find((item) => {
      return item.ratePlanId === ratePlanId;
    });
  };

  availableRatePlans = (): RatePlan[] => {
    if (!this.state.ratePlans) {
      return [];
    }

    const hasDiscounts = this.props.studioClient?.discounts?.length > 0;
    if (!hasDiscounts) {
      return this.state.ratePlans;
    }

    return this.state.ratePlans
      .map((ratePlan) => ({
        ...ratePlan,
        discount: this.findDiscount(ratePlan._id),
      }))
      .filter((ratePlan) => ratePlan.discount);
  };

  getLinkToBuy = (ratePlan: RatePlan): string => {
    let url = routes.bookingRatePlan({
      ownerId: this.props.owner._id,
      ratePlanId: ratePlan._id,
    });
    if (this.props.studioClient) {
      url += `?studioClientId=${this.props.studioClient._id}&paymentSource=${this.props.paymentSource}`;
    }
    return url;
  };

  renderDivider = (): JSX.Element => {
    const backgroundColor = this.getThemeColor(0) ? this.getThemeColor(0) : '#fbfbfb';
    return <Divider style={{ backgroundColor }} />;
  };

  render = (): JSX.Element => {
    if (this.state.loadingRatePlans) {
      return LayoutHelper.renderLoader();
    }

    const ratePlans = this.availableRatePlans();
    return (
      <Grid item xs={12}>
        <Paper>
          <Card elevation={0}>
            <CardHeader title={this.props.translate('studio.ratePlans')}></CardHeader>
            {this.renderDivider()}
            <CardContent className={this.props.classes.pricePaper}>
              {ratePlans.length === 0 && (
                <Typography align="center">{this.props.translate('myClients.noRatePlans')}</Typography>
              )}
              {ratePlans.map(this.renderRatePlan)}
            </CardContent>
          </Card>
        </Paper>
      </Grid>
    );
  };

  renderRatePlan = (ratePlan: RatePlan): JSX.Element => {
    const domain = this.state.ownerDomain || this.props.domain.domain;
    const currencyCode = domain.currencyCode;
    let price = ratePlan.price;
    if (ratePlan.discount) {
      price = calculatePriceAfterDiscount(ratePlan.price, ratePlan.discount.discount, ratePlan.discount.discountType);
    }

    const buttonClassName = `${this.props.paymentSource}Button` as keyof typeof this.props.classes;
    return (
      <Box key={ratePlan._id} style={{ borderLeft: `2px solid ${ratePlan.color}` }}>
        <Grid container className={this.props.classes.priceRow} spacing={2} justify="space-between" alignItems="center">
          <Grid item xs={12} sm={'auto'}>
            <Typography>{ratePlan.name}: </Typography>
            <Typography variant="body2" color="textSecondary">
              {ratePlan.description}
            </Typography>
            {ratePlan.discount && ratePlan.discount.discount > 0 && (
              <Typography variant="caption" color="textPrimary">
                ({this.props.translate('myClients.withDiscount')}: {formatDiscount(ratePlan.discount, currencyCode)})
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={'auto'}>
            {this.props.owner?.liqPayPayment ||
            this.props.owner?.wayForPayPayment ||
            this.props.owner?.monobankAcquiringPayment ||
            this.props.owner?.organizationSettings?.stripeConfigs?.apiKey ? (
              <Button
                fullWidth
                component={Link}
                target="_blank"
                href={this.getLinkToBuy(ratePlan)}
                color="secondary"
                variant="contained"
                className={this.props.classes[buttonClassName]}
              >
                {this.props.paymentSource === LIQ_PAY_PAYMENT_SOURCE && (
                  <img
                    src="https://static.liqpay.ua/buttons/logo-small.png"
                    style={{ marginRight: '7px', verticalAlign: 'middle' }}
                    alt="LiqPay"
                  />
                )}
                {this.props.paymentSource === STRIPE_PAYMENT_SOURCE && (
                  <img
                    src="https://s3.eu-central-1.amazonaws.com/img.voopty.net/Stripe_icon_-_square.svg"
                    style={{
                      marginRight: '7px',
                      width: '30px',
                      verticalAlign: 'middle',
                    }}
                    alt="Stripe"
                  />
                )}
                {this.props.paymentSource === MONOBANK_PAYMENT_SOURCE && (
                  <img
                    src="https://s3.eu-central-1.amazonaws.com/img.voopty.net/monobank-logo.svg"
                    style={{
                      marginRight: '7px',
                      width: '30px',
                      verticalAlign: 'middle',
                    }}
                    alt="Monobank"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                {this.props.translate('studio.class.pay')} {formatPrice(price, currencyCode)}
              </Button>
            ) : (
              <Typography noWrap={true} align="right" variant="h6">
                {formatPrice(price, currencyCode)}
              </Typography>
            )}
          </Grid>
        </Grid>
        {this.renderDivider()}
      </Box>
    );
  };
}

interface RootState {
  locale: any;
  user: any;
  domain: any;
}

const mapStateToProps = (state: RootState) => ({
  translate: getTranslate(state.locale),
  currentLanguage: getActiveLanguage(state.locale).code,
  user: state.user,
  domain: state.domain,
});

const mapDispatchToProps = () => {
  return {};
};

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      marginLeft: 300,
      top: '56px',
      width: '100%',
      opacity: '0.7',
    },
    studioHeader: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      textAlign: 'center',
      '& > li': {
        display: 'inline-block',
      },
    },
    wayForPayButton: {
      background: '#0488cd',
      borderRadius: '14px',
      padding: '10px',
      fontWeight: 'bold',
      color: '#ffffff',
      verticalAlign: 'middle',
    },
    liqPayButton: {
      padding: '7px 20px',
      color: '#fff',
      fontWeight: '600',
      borderRadius: '2px',
      background: 'rgba(122,183,43,1)',
    },
    stripeButton: {
      padding: '7px 20px',
      color: '#fff',
      fontWeight: '600',
      borderRadius: '2px',
      background: 'rgb(98,90,253)',
    },
    monobankButton: {
      padding: '7px 20px',
      color: '#fff',
      fontWeight: '600',
      borderRadius: '2px',
      background: '#000000',
    },
    headerLink: {
      display: 'block',
      color: theme.palette.primary.light,
      transition: 'color 0.25s',
      lineHeight: '1.5',
      padding: theme.spacing(1.5),
      letterSpacing: '0.05rem',
      position: 'relative',
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.primary.light,
      },
      '& span': {
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        height: 0,
        opacity: 0,
        transition: 'all 0.3s',
      },
      '&:hover span': {
        bottom: '0',
        height: '3px',
        opacity: '1',
      },
    },
    toolbarButtons: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
    },
    avatar: {
      width: theme.spacing(3.5),
      height: theme.spacing(3.5),
    },
    orangeLink: {
      color: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    studioMainInfo: {
      minHeight: '100vh',
      padding: '120px 15px 70px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    infoWrap: {
      padding: theme.spacing(4, 3, 8),
      maxWidth: '740px',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      color: theme.palette.primary.dark,
      position: 'relative',
    },
    headerRate: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      margin: theme.spacing(1, 0),
      padding: theme.spacing(1),
      borderRadius: '6px 6px 0 6px',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.light,
      position: 'absolute',
      bottom: '100%',
      left: 0,
    },
    ratingValue: {
      fontSize: '26px',
      fontWeight: 'bold',
      margin: theme.spacing(0, 2, 0, 0.5),
    },
    reviewCount: {
      textAlign: 'left',
      fontSize: '12px',
      lineHeight: '1.4',
    },
    alertWarning: {
      fontSize: '12px',
      marginTop: theme.spacing(2),
      padding: theme.spacing(1, 2),
      border: '2px solid',
      borderColor: theme.palette.primary.main,
      borderRadius: 0,

      '& > div h1': {
        margin: theme.spacing(0, 0, 2),
      },
      '& > div p': {
        margin: theme.spacing(0, 0, 1),
      },
    },
    pricePaper: {
      padding: '0 !important',
    },
    priceRow: {
      padding: theme.spacing(1, 2),
      margin: 0,
    },
    infoIcons: {
      padding: theme.spacing(1, 3),
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: 'rgba(30, 32, 36, 0.4)',

      '& > ul': {
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },

      '& > ul li': {
        margin: theme.spacing(0, 1, 0, 0),
        width: '32px',
        height: '32px',
        backgroundSize: 'contain',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      },
    },
    promoLink: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      transition: 'color 0.25s',
      cursor: 'pointer',
      textDecoration: 'none',
      height: '32px',

      '&:hover': {
        color: theme.palette.primary.light,
      },

      '& img': {
        marginRight: theme.spacing(1),
      },
    },
    expansionPanel: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.primary.light,
    },
  });

export default withStyles(styles)(withSnackbar(connect(mapStateToProps, mapDispatchToProps)(RatePlansView)));
